import React from 'react';
import {isMobileOnly} from 'react-device-detect';
import {WebcamProps, WebcamRefTypes} from '../../Webcam';
import {IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB} from 'utils/constants';
import MobileWebcam, {MobileCameraProps} from '../../MobileWebcam';
import {WebcamStyled} from '../styled';
import {PlaceholderImage} from './styled';

export {type MobileScreenshotPayLoad} from '../../MobileWebcam';

const VisibleHelperImageTimeoutMS = 1250;

type TakePhotoProps = WebcamProps &
  MobileCameraProps & {
    mobileHelperText?: React.ReactNode | string;
    mobileHelperTitle?: React.ReactNode | string;
    placeholderImage?: string;
  };
const TakePhoto = React.forwardRef<WebcamRefTypes, TakePhotoProps>(
  (
    {
      placeholderImage,
      onScreenshot,
      mobileHelperText,
      mobileHelperTitle,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [isPlaceholderImageVisible, setIsPlaceholderImageVisible] =
      React.useState(false);

    const onUserMedia = React.useCallback(() => {
      setIsPlaceholderImageVisible(true);
    }, []);

    const webcamProps = React.useMemo(
      () => ({
        ...props,
        minScreenshotW: 800,
        minScreenshotH: 600,
        screenshotMaxSize: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
        imageSmoothing: false,
        onUserMedia,
      }),
      [onUserMedia, props],
    );

    React.useEffect(() => {
      if (!isPlaceholderImageVisible) return;
      const timeout = setTimeout(() => {
        setIsPlaceholderImageVisible(false);
      }, VisibleHelperImageTimeoutMS);

      return () => {
        clearTimeout(timeout);
      };
    }, [isPlaceholderImageVisible]);

    if (isMobileOnly) {
      return (
        <MobileWebcam
          ref={ref}
          disabled={disabled}
          onScreenshot={onScreenshot}
          text={mobileHelperText}
          title={mobileHelperTitle}
          cropToScanArea={!props.roundedScanArea}
          placeholderImage={
            placeholderImage ? (
              <PlaceholderImage
                $visible={isPlaceholderImageVisible}
                src={placeholderImage}
                alt="Placeholder"
              />
            ) : null
          }
          {...webcamProps}
        />
      );
    }

    return (
      <WebcamStyled
        ref={ref}
        cropToScanArea
        placeholderImage={
          placeholderImage ? (
            <PlaceholderImage
              $visible={isPlaceholderImageVisible}
              src={placeholderImage}
              alt="Placeholder"
            />
          ) : null
        }
        {...webcamProps}
      />
    );
  },
);

export {TakePhoto};
